import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, startOfMonth, startOfWeek } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { FormField } from '@/types/api/form'
import type { RecordListItem } from '@/types/api/record'
import { cn } from '@/utils/cn'

import { parseDay, rawValue, recordHref, recordTitle } from './viewUtils'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Month grid; each record sits on the day held by the date field. The page fetches the visible month. */
export function CalendarView({ records, dateField, month, onMonthChange }: {
  records: RecordListItem[]
  dateField: FormField | undefined
  month: Date
  onMonthChange: (month: Date) => void
}) {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
  })
  const byDay = new Map<string, RecordListItem[]>()
  if (dateField) {
    for (const record of records) {
      const day = parseDay(rawValue(record, dateField.key))
      if (!day) continue
      const key = format(day, 'yyyy-MM-dd')
      byDay.set(key, [...(byDay.get(key) ?? []), record])
    }
  }

  return (
    <Card className="gap-0 overflow-hidden shadow-none">
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <h3 className="text-sm font-semibold">{format(month, 'MMMM yyyy')}</h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" aria-label="Previous month" onClick={() => onMonthChange(addMonths(month, -1))}><ChevronLeft className="size-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => onMonthChange(new Date())}>Today</Button>
          <Button variant="ghost" size="icon" aria-label="Next month" onClick={() => onMonthChange(addMonths(month, 1))}><ChevronRight className="size-4" /></Button>
        </div>
      </div>
      {!dateField ? (
        <p className="p-8 text-center text-sm text-muted-foreground">Pick a Date field to place records on this calendar.</p>
      ) : (
        <div className="grid grid-cols-7 [&>*]:border-b [&>*]:border-r [&>*]:border-border">
          {WEEKDAYS.map((d) => <div key={d} className="bg-muted/40 px-2 py-1 text-xs font-semibold uppercase text-muted-foreground">{d}</div>)}
          {days.map((day) => {
            const items = byDay.get(format(day, 'yyyy-MM-dd')) ?? []
            return (
              <div key={day.toISOString()} className={cn('min-h-24 space-y-1 p-1.5', !isSameMonth(day, month) && 'bg-muted/30 text-muted-foreground')}>
                <div className="text-xs">{format(day, 'd')}</div>
                {items.slice(0, 3).map((r) => (
                  <Link key={r.recordId} to={recordHref(r)} className="block truncate rounded bg-primary/10 px-1.5 py-0.5 text-xs text-foreground hover:bg-primary/20">
                    {recordTitle(r)}
                  </Link>
                ))}
                {items.length > 3 && <div className="text-xs text-muted-foreground">+{items.length - 3} more</div>}
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}
